import { useEffect } from 'react';
import type { IOrderCurrentDTOData } from '~/dtos/ProdegaApiWebshop-api-typings-generated';

export type RemovedCartPositionTrackingData = {
  articleNumber: string;
  quantity: number;
  articleDescription: string;
};

type ShoppingCartBroadcastPayload =
  | { type: 'CART_SUCCESSFULLY_SENT'; context: { order: IOrderCurrentDTOData } }
  | {
      type: 'ADD_INTENT_PROCESSED';
      context: {
        successfullyAdded: boolean;
        addedWithAvailabilityOptions: boolean;
        articleNumber: string;
        quantity: number;
        isExternalArticle: boolean;
        articleDescription: string;
      };
    }
  | { type: 'POSITIONS_REMOVED'; context: { positions: RemovedCartPositionTrackingData[] } };
type ShoppingCartBroadcastEvent = ShoppingCartBroadcastPayload['type'];

type ShoppingCartBroadcastListener = (b: ShoppingCartBroadcastPayload) => void;

const SHOPPING_CART_BROADCAST_LISTENERS: Set<ShoppingCartBroadcastListener> = new Set(); // Singleton

export function subscribeToShoppingCartBroadcast(fn: ShoppingCartBroadcastListener) {
  SHOPPING_CART_BROADCAST_LISTENERS.add(fn);
  return () => {
    // Unsubscribe function
    SHOPPING_CART_BROADCAST_LISTENERS.delete(fn);
  };
}

export function useListenToShoppingCartBroadcast<E extends ShoppingCartBroadcastEvent>(
  event: E,
  cartBroadcastListener: Extract<ShoppingCartBroadcastPayload, { type: E }> extends { context: infer C }
    ? C extends undefined
      ? () => void
      : (c: C) => void
    : never
) {
  useEffect(
    () =>
      subscribeToShoppingCartBroadcast(payload => {
        if (payload.type === event) {
          cartBroadcastListener(payload.context);
        }
      }),
    [cartBroadcastListener] // eslint-disable-line react-hooks/exhaustive-deps
  );
  // unsubscribe wird via effect cleanup aufgerufen
}

export function publishShoppingCartBroadcast<E extends ShoppingCartBroadcastEvent>(
  e: E,
  ...args: Extract<ShoppingCartBroadcastPayload, { type: E }> extends { context: infer C }
    ? C extends undefined
      ? []
      : [cartContext: C]
    : never
) {
  const cartContext = args[0];
  const broadcastPayload = { type: e, context: cartContext } as ShoppingCartBroadcastPayload;
  SHOPPING_CART_BROADCAST_LISTENERS.forEach(l => l(broadcastPayload));
}
